//Location Details, Customer Equipment and Service History Modules

import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Nav,
  NavItem,
  TabContent,
  TabPane,
  NavLink,
  FormGroup,
  Label,
  Container,
  Badge,
  CardHeader,
  Button,
  Input,
  CardFooter,
  UncontrolledTooltip,
} from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CommonTable from "components/ReactTable/Table";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import {
  REACT_APP_GOOGLE_MAPS_API_KEY,
  fixedWidthCustomStyle,
  sellerOptions,
  DEFAULT_RECORDS_PER_PAGE,
  WORK_ORDER,
} from "components/Common/constants";
import RightPanelCard from "components/Common/RightPanelCard";
import Select from "react-select";
import { showAlert } from "components/Common/commonFunctions";
import NotificationAlert from "react-notification-alert";
import Breadcrumbs from "components/Common/BreadCrumbs";
import { getCustomerLocationById } from "actions/customerLocation";
import {
  urlDecodedId,
  urlEncodedId,
} from "components/Common/encodeDecodeFunction";
import { getManufacturerList } from "actions/manufacturer";
import { getProductCategoryList } from "actions/productCategories";
import { getProductList } from "actions/product";
import { getCustomerById } from "actions/customer";
import {
  getCustomerEquipmentList,
  addCustomerEquipment,
  getCustomerEquipmentById,
  editCustomerEquipment,
  deleteCustomerEquipment,
} from "actions/customerEquipment";
import { useDispatch, useSelector } from "react-redux";
import CommonEquipmentForm from "./CommonEquipmentForm";
import {
  convertUIToISODate,
  addYearsToISODate,
  formatDateWithTZ,
} from "components/Common/commonFunctions";
import { getCustomerServiceList } from "actions/customer";

const initialEquipmentFields = {
  manufacturer: { value: null, error: "" },
  product: { value: null, error: "" },
  category: { value: null, error: "" },
  model: { value: null, error: "" },
  serialNo: { value: "", error: "" },
  seller: { value: null, error: "" },
  assetTag: { value: "", error: "" },
  warranty: { value: false, error: "" },
  standardWarranty: { value: false, error: "" },
  warrantyStartDate: { value: "", error: "" },
  warrantyEndDate: { value: "", error: "" },
  purchaseDate: { value: "", error: "" },
};

function LocationDetails() {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: REACT_APP_GOOGLE_MAPS_API_KEY,
  });
  const { customerID: encCustomerId, locationID: encLocationId } = useParams();
  const decodedCustomerId = urlDecodedId(encCustomerId);
  const decodedLocationId = urlDecodedId(encLocationId);
  const notificationAlertRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const data = location.state || {};
  const [locationDetails, setLocationDetails] = useState({
    customerName: null,
    postCode: "",
  });
  const [equipmentPage, setEquipmentPage] = useState(1);
  const [historyPageNum, sethistoryPageNum] = useState(1);
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("tab") || "details";
  });
  const [map, setMap] = useState(null);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [modal, setModal] = useState({
    isOpen: false,
    mode: "",
    data: null,
  });
  const [equipmentFilters, setEquipmentFilters] = useState({
    searchText: "",
    manufacturer: null,
    category: null,
    product: null,
    model: null,
    seller: null,
    warranty: null,
    standardWarranty: null,
    filterParams: { filters: [], searchString: "" },
  });
  const [formData, setFormData] = useState(initialEquipmentFields);
  const [equipmentListState, setEquipmentListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [serviceListState, setServiceListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [showLoader, setShowLoader] = useState(false);

  const equipmentListData = useSelector(
    (state) => state.customerEquipmentList?.customerEquipment || {},
  );
  const serviveHistoryData = useSelector(
    (state) => state.customer?.customerServiceHistory || {},
  );
  const manufacturersList = useSelector(
    (state) => state.manufacturer?.manufacturerList?.data?.manufacturer || [],
  );

  const productCategoryListData = useSelector(
    (state) =>
      state.productCategory?.productCategoryList?.data?.productCategory || [],
  );

  const productListData = useSelector(
    (state) => state.products?.productList?.data?.product || [],
  );

  const defaultCenter = { lat: 24.7136, lng: 46.6753 };

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const toggleTab = async (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);

      navigate(`${location.pathname}?tab=${tab}`, { replace: true });
    }
  };

  useEffect(() => {
    getManufacturerList(dispatch);
    getProductCategoryList(dispatch);
    getProductList(dispatch);
  }, [dispatch]);

  // handle equipment list response
  useEffect(() => {
    if (activeTab !== "equipments") return;

    if (!equipmentListData) return;

    if (equipmentListData.code === 200) {
      const list = equipmentListData.data?.CustomerEquipment || [];

      setEquipmentListState({
        isLoading: false,
        error: "",
        noRecords: list.length === 0,
        showLoaderOnClick: false,
      });
    } else {
      setEquipmentListState({
        isLoading: false,
        error: equipmentListData?.message || "Failed to load equipment list.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [equipmentListData, activeTab]);

  useEffect(() => {
    if (activeTab !== "history") return;

    if (!serviveHistoryData) return;

    if (serviveHistoryData.code === 200) {
      const list = serviveHistoryData.data?.job || [];

      setServiceListState({
        isLoading: false,
        error: "",
        noRecords: list.length === 0,
        showLoaderOnClick: false
      });
    } else {
      setServiceListState({
        isLoading: false,
        error: serviveHistoryData?.message || "Failed to load service history.",
        noRecords: false,
        showLoaderOnClick: false
      });
    }
  }, [serviveHistoryData, activeTab]);

  useEffect(() => {
    if (isLoaded && data.address) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: data.address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          setMarkerPosition({
            lat: location.lat(),
            lng: location.lng(),
          });
        }
      });
    }
  }, [isLoaded, data.address]);

  useEffect(() => {
    if (!decodedCustomerId || !decodedLocationId) return;
    getLocationDetails(decodedCustomerId, decodedLocationId);
  }, [decodedCustomerId, decodedLocationId]);

  useEffect(() => {
    if (!modal.isOpen) {
      setFormData(structuredClone(initialEquipmentFields));
    }
  }, [modal.isOpen]);
  useEffect(() => {
    if (activeTab !== "equipments") return;

    const { filters, searchString, warranty } =
      equipmentFilters.filterParams || {};

    setEquipmentListState((prev) => ({ ...prev, isLoading: true, error: "", noRecords: false }));

    getCustomerEquipmentList(
      dispatch,
      decodedCustomerId,
      decodedLocationId,
      equipmentPage,
      filters?.length ? JSON.stringify(filters) : "",
      searchString,
      warranty,
    );
  }, [activeTab, equipmentPage, equipmentFilters.filterParams]);

  useEffect(() => {
    if (activeTab !== "history") return;
    setServiceListState((prev) => ({ ...prev, isLoading: true, error: "", noRecords: false }));
    getCustomerServiceList(dispatch, decodedLocationId, historyPageNum);
  }, [activeTab, historyPageNum]);

  useEffect(() => {
    if (!decodedCustomerId) return;

    const fetchCustomerName = async () => {
      const res = await getCustomerById(decodedCustomerId);
      if (res?.code === 200) {
        setLocationDetails((prev) => ({
          ...prev,
          customerName:
            `${res?.data?.firstName} ${res?.data?.lastName}` || "Customer",
        }));
      }
    };

    fetchCustomerName();
  }, [decodedCustomerId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get("tab");

    if (!tabFromUrl) {
      navigate(`${location.pathname}?tab=${activeTab}`, { replace: true });
    }
  }, []);

  const getLocationDetails = async (customerID, locationID) => {
    try {
      if (!customerID || !locationID) return;

      const res = await getCustomerLocationById(
        decodedCustomerId,
        decodedLocationId,
      );

      if (res.code === 200) {
        const loc = res.data;

        setLocationDetails((prev) => ({
          ...prev,
          outlet: loc.outletName,
          address: loc.address,
          region: loc.region?.name,
          city: loc.city?.name,
          name: loc.contactName,
          email: loc.email,
          mobile: loc.contactNo,
          alt_name: loc.alternateContactName,
          alt_email: loc.alternateEmail,
          alt_mobile: loc.alternateContactNo,
          postCode: loc.postCode,
        }));

        if (loc.mapDetails?.coordinates) {
          setMarkerPosition(loc.mapDetails.coordinates);
        }

        return res;
      } else {
        return res;
      }
    } catch (error) {
      return (
        error?.response?.data || {
          code: 500,
          message: "Failed to load location",
        }
      );
    }
  };

  const buildEquipmentFilterParams = () => {
    const filterParams = [];
    let warranty = "";

    if (equipmentFilters.manufacturer)
      filterParams.push({
        field: "ManufacturerID",
        condition: "eq",
        filterValues: [String(equipmentFilters.manufacturer.ID)],
      });

    if (equipmentFilters.category)
      filterParams.push({
        field: "ProductCategoryID",
        condition: "eq",
        filterValues: [String(equipmentFilters.category.ID)],
      });

    if (equipmentFilters.product)
      filterParams.push({
        field: "ProductID",
        condition: "eq",
        filterValues: [String(equipmentFilters.product.ID)],
      });

    if (equipmentFilters.model)
      filterParams.push({
        field: "Model",
        condition: "eq",
        filterValues: [String(equipmentFilters.model.modelNo)],
      });

    if (equipmentFilters.seller)
      filterParams.push({
        field: "Seller",
        condition: "eq",
        filterValues: [String(equipmentFilters.seller.value)],
      });

    if (equipmentFilters.warranty) {
      warranty = equipmentFilters.warranty.value ? "YES" : "NO";
    }

    if (equipmentFilters.standardWarranty) {
      const stdValue = equipmentFilters.standardWarranty.value ? "YES" : "NO";
      filterParams.push({
        field: "StandardWarranty",
        condition: "eq",
        filterValues: [stdValue],
      });
    }

    return {
      filters: filterParams,
      searchString: equipmentFilters.searchText || "",
      warranty: equipmentFilters.warranty
        ? equipmentFilters.warranty.value
          ? "YES"
          : "NO"
        : "",
    };
  };

  const handleEquipmentBlur = (field) => {
    setFormData((prev) => {
      const updated = { ...prev };

      switch (field) {
        case "manufacturer":
        case "category":
        case "product":
        case "seller":
          updated[field].error = !prev[field].value
            ? `Please select ${field}`
            : "";
          break;

        case "model":
          updated.model.error = !prev.model.value ? "Please select model" : "";
          break;

        case "serialNo":
          updated.serialNo.error =
            prev.serialNo.value && prev.serialNo.value.trim() !== ""
              ? ""
              : "Please enter serial no";
          break;

        case "warrantyStartDate":
          if (prev.warranty.value) {
            updated.warrantyStartDate.error = prev.warrantyStartDate.value
              ? ""
              : "Please select start date";
          }
          break;

        case "warrantyEndDate":
          if (prev.warranty.value && !prev.standardWarranty.value) {
            updated.warrantyEndDate.error = prev.warrantyEndDate.value
              ? ""
              : "Please select end date";
          }
          break;

        default:
          break;
      }

      return updated;
    });
  };

  const handleSaveEquipment = async () => {
    if (!validateEquipmentForm()) return;

    setShowLoader(true);
    const payload = {
      customerId: Number(decodedCustomerId),
      customerLocationId: Number(decodedLocationId),
      manufacturerId: Number(formData.manufacturer.value?.ID) || null,
      productCategoryId: Number(formData.category.value?.ID) || null,
      productId: Number(formData.product.value?.ID) || null,
      //modelId: formData.model.value?.value || null,
      purchaseDate: formatDateWithTZ(formData.purchaseDate.value),
      serialNo: formData.serialNo.value,
      assetTag: formData.assetTag.value,
      seller: formData.seller.value?.value || null,
      status: "ACTIVE",
      //warranty: formData.warranty.value,
      //standardWarranty: formData.standardWarranty.value,
      warrantyStartDate: formatDateWithTZ(formData.warrantyStartDate.value),
      warrantyEndDate: formatDateWithTZ(formData.warrantyEndDate.value),
    };

    let res = {};

    if (modal.mode === "Edit") {
      res = await editCustomerEquipment(modal.data.ID, payload);
    } else {
      res = await addCustomerEquipment(payload);
    }

    if (res.code === 200) {
      notify(
        "success",
        res?.message ||
        `Equipment ${modal.mode === "Edit" ? "updated" : "added"} successfully`,
      );
      handleCloseModal();
      getCustomerEquipmentList(
        dispatch,
        decodedCustomerId,
        decodedLocationId,
        equipmentPage,
        equipmentFilters.filterParams.filters?.length
          ? JSON.stringify(equipmentFilters.filterParams.filters)
          : "",
        equipmentFilters.filterParams.searchString,
      );
    } else {
      const message = Array.isArray(res?.message)
        ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
        : res?.message || "Failed!";
      notify("danger", message);
    }
    setShowLoader(false);
  };

  const validateEquipmentForm = () => {
    let isValid = true;
    const updated = { ...formData };

    const requiredDropdowns = [
      { key: "manufacturer", label: "manufacturer" },
      { key: "category", label: "category" },
      { key: "product", label: "product" },
      { key: "seller", label: "seller" },
      { key: "model", label: "model" },
    ];

    requiredDropdowns.forEach(({ key, label }) => {
      if (!formData[key].value) {
        updated[key].error = `Please select ${label}`;
        isValid = false;
      } else {
        updated[key].error = "";
      }
    });

    if (!formData.serialNo.value || formData.serialNo.value.trim() === "") {
      updated.serialNo.error = "Please enter serial no";
      isValid = false;
    } else {
      updated.serialNo.error = "";
    }

    if (formData.warranty.value) {
      if (!formData.warrantyStartDate.value) {
        updated.warrantyStartDate.error = "Start date is required";
        isValid = false;
      } else {
        updated.warrantyStartDate.error = "";
      }

      if (!formData.standardWarranty.value && !formData.warrantyEndDate.value) {
        updated.warrantyEndDate.error = "End date is required";
        isValid = false;
      } else {
        updated.warrantyEndDate.error = "";
      }
    }

    setFormData(updated);
    return isValid;
  };

  const handleEquipmentSearch = () => {
    const params = buildEquipmentFilterParams();
    setEquipmentFilters((prev) => ({ ...prev, filterParams: params }));
    setEquipmentListState((prev) => ({ ...prev, showLoaderOnClick: true }));
  };

  const handleEquipmentClear = () => {
    setEquipmentFilters({
      searchText: "",
      manufacturer: null,
      category: null,
      product: null,
      model: null,
      seller: null,
      warranty: null,
      standardWarranty: null,
      filterParams: { filters: [], searchString: "" },
    });
  };

  const filterCategoryOptions = useMemo(() => {
    if (!equipmentFilters.manufacturer) return [];
    return productCategoryListData.filter(
      (c) => c.manufacturerResponse?.ID === equipmentFilters.manufacturer.ID,
    );
  }, [equipmentFilters.manufacturer, productCategoryListData]);

  const filterProductOptions = useMemo(() => {
    if (!equipmentFilters.category) return [];

    return productListData.filter(
      (p) =>
        p.productCategory?.ID === equipmentFilters.category.ID &&
        p.manufacturer?.ID === equipmentFilters.manufacturer.ID,
    );
  }, [
    equipmentFilters.category,
    equipmentFilters.manufacturer,
    productListData,
  ]);

  const filterModelOptions = useMemo(() => {
    if (!equipmentFilters.product) return [];
    return [{ modelNo: equipmentFilters.product.modelNo }];
  }, [equipmentFilters.product]);

  const columns = [
    { Header: "Sr. no.", accessor: "srNo", className: "col-srNo" },
    { Header: "Manufacturer", accessor: "manufacturer", className: "col-manufacturer" },
    {
      Header: "Category", accessor: "productCategory", className: "col-productcategory",
      Cell: ({ value }) => <span>{value?.category || "-"}</span>,
    },
    { Header: "Product", accessor: "product", className: "col-product" },
    { Header: "Model", accessor: "model", className: "col-model" },
    {
      Header: "Serial No.",
      accessor: "serialNo",
      className: "col-model",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Seller",
      accessor: "seller",
      className: "col-model",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Asset Tag",
      accessor: "assetTag",
      className: "col-model",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Under Warranty",
      accessor: "warranty",
      className: "col-warranty",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Warranty Details",
      accessor: "standardWarranty",
      className: "col-warranty-details",
      Cell: ({ row }) => {
        const {
          warranty,
          standardWarranty,
          warrantyStartDate,
          warrantyEndDate,
        } = row.original;
        return (
          <div className="text-left">
            {warranty ? (
              <>
                <div>
                  <span className="text-muted mr-2">Standard Warranty:</span>
                  {standardWarranty || "-"}
                </div>
                {warrantyStartDate && (
                  <div>
                    <span className="text-muted mr-2">Start Date:</span>
                    {new Date(warrantyStartDate).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </div>
                )}
                {warrantyEndDate && (
                  <div>
                    <span className="text-muted mr-2">End Date:</span>
                    {new Date(warrantyEndDate).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </div>
                )}
              </>
            ) : (
              "-"
            )}
          </div>
        );
      },
    },
    {
      Header: "Actions",
      accessor: "actions",
      className: "text-center",
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="actions-right d-flex justify-content-start">
          <div>
            <Button
              className="btn-icon icon-info"
              color="link"
              id={`edit${row.original.ID}`}
              size="sm"
              onClick={() => handleEquipments("Edit", row.original)}
            >
              <i className="tim-icons icon-pencil tim-md" />
            </Button>
            <UncontrolledTooltip delay={0} target={`edit${row.original.ID}`}>
              Edit
            </UncontrolledTooltip>
          </div>
          <span className="mx-1 mt-1 text-muted">|</span>
          <div>
            <Button
              className="btn-icon icon-danger"
              color="link"
              id={`reset${row.original.ID}`}
              size="sm"
              onClick={() => handleDeleteEquipment(row.original)}
            >
              <i className="tim-icons icon-trash-simple tim-md" />
            </Button>
            <UncontrolledTooltip delay={0} target={`reset${row.original.ID}`}>
              Delete
            </UncontrolledTooltip>
          </div>
        </div>
      ),
    },
  ];

  const history = [
    {
      ID: 1,
      jobNo: "162019",
      type: "Preventive Maintenance",
      description: "JTM_Coldroom PPM Q1 2025@PF Chang's, Red Sea Mall Jeddah",
      name: "JoJo",
      starts: "05 Mar 2025 10:00 AM",
      end: "05 Mar 2025 02:00 PM",
      Status: "INVOICE (PAID)",
    },
    {
      ID: 2,
      jobNo: "159554",
      type: "Preventive Maintenance",
      description:
        "JTM_January 2025 Equipments PPM@PF Chang's, Red Sea Mall Jeddah",
      name: "JoJo",
      starts: "25 Feb 2025 08:00 AM",
      end: "25 Feb 2025 10:00 PM",
      Status: "INVOICE (PAID)",
    },
    {
      ID: 3,
      jobNo: "156742",
      type: "Regular Maintenance",
      description:
        "2014232013 Requested By: RED SEA MALL-P.F CHANG'S Description: Pantry down chiller is not working. Requested by Omar. Alshaya Priority: 2",
      name: "Arlan",
      starts: "15 Nov 2024 10:00 AM",
      end: "15 Nov 2024 12:00 PM",
      Status: "INVOICE (PAID)",
    },
    {
      ID: 4,
      jobNo: "155960",
      type: "Regular Maintenance",
      description:
        "2014232192 Requested By: RED SEA MALL-P.F CHANG'S Description: DISH MACHINE IS NOT WORKING. CONTACT DANIA - PHONE Alshaya Priority: 3",
      name: "Naqash",
      starts: "30 Oct 2024 02:00 PM",
      end: "30 Oct 2024 04:00 PM",
      Status: "INVOICE (PAID)",
    },
    {
      ID: 5,
      jobNo: "155710",
      type: "Regular Maintenance",
      description:
        "2014232192 Requested By: RED SEA MALL-P.F CHANG'S Description: DISH MACHINE IS NOT WORKING. CONTACT DANIA - PHONE Alshaya Priority: 3",
      name: "Jose JR",
      starts: "27 Oct 2024 12:00 PM",
      end: "27 Oct 2024 02:00 PM",
      Status: "INVOICE (PAID)",
    },
    {
      ID: 6,
      jobNo: "155310",
      type: "Regular Maintenance",
      description:
        "2014219623 Requested By: RED SEA MALL-P.F CHANG'S Description: WALK CHILLER TEMPERATURE PROBLEM. NOT WORKING PROPERLY. REQUESTED BY DANIA. Alshaya Priority: 2",
      name: "Jose JR",
      starts: "20 Oct 2024 08:00 AM",
      end: "20 Oct 2024 10:00 AM",
      Status: "INVOICE (PAID)",
    },
    {
      ID: 7,
      jobNo: "154999",
      type: "Preventive Maintenance",
      description:
        "JTM_COLDROOM October PPM(Q4)@PF Chang's, Red Sea Mall Jeddah",
      name: "Mohamed",
      starts: "19 Sep 2024 02:00 PM",
      end: "19 Sep 2024 04:00 PM",
      Status: "INVOICE (PAID)",
    },
  ];

  const handleEquipments = async (mode, data) => {
    if (mode === "Add" || !data) {
      setFormData(structuredClone(initialEquipmentFields));
      setModal({ isOpen: true, mode, data: null });
      return;
    }

    try {
      const res = await getCustomerEquipmentById(data.ID);

      if (res.code !== 200) return;

      const eq = res.data;

      const ws = eq.warrantyStartDate;
      const we = eq.warrantyEndDate;

      const isEmpty = (d) => !d || d === "01 Jan 0001";

      let warranty = false;
      let standardWarranty = false;
      let warrantyStartDate = "";
      let warrantyEndDate = "";

      // CASE 1: Both dates empty → no warranty
      if (isEmpty(ws) && isEmpty(we)) {
        warranty = false;
        standardWarranty = false;
        warrantyStartDate = "";
        warrantyEndDate = "";
      } else {
        // CASE 2: Dates exist → calculate difference
        const start = new Date(ws);
        const end = new Date(we);

        const diffYears = (end - start) / (1000 * 60 * 60 * 24 * 365);

        if (diffYears === 2) {
          // CONDITION B: 2 years → standard warranty
          warranty = true;
          standardWarranty = true;
          warrantyStartDate = convertUIToISODate(ws);
          warrantyEndDate = "";
        } else {
          // CONDITION A: Not 2 years → regular warranty
          warranty = true;
          standardWarranty = false;
          warrantyStartDate = convertUIToISODate(ws);
          warrantyEndDate = convertUIToISODate(we);
        }
      }

      setFormData({
        ...initialEquipmentFields,
        manufacturer: {
          value:
            manufacturersList.find((m) => m.ID === eq.manufacturer?.ID) || null,
          error: "",
        },
        category: {
          value:
            productCategoryListData.find(
              (c) => c.ID === eq.productCategory?.ID,
            ) || null,
          error: "",
        },
        product: {
          value: productListData.find((p) => p.ID === eq.product?.ID) || null,
          error: "",
        },
        model: {
          value: eq.product?.modelNo ? { modelNo: eq.product.modelNo } : null,
          error: "",
        },
        serialNo: { value: eq.serialNo || "", error: "" },
        assetTag: { value: eq.assetTag || "", error: "" },
        seller: {
          value: sellerOptions.find((s) => s.value === eq.seller) || null,
          error: "",
        },
        warranty: { value: warranty, error: "" },
        standardWarranty: { value: standardWarranty, error: "" },
        warrantyStartDate: { value: warrantyStartDate, error: "" },
        warrantyEndDate: { value: warrantyEndDate, error: "" },
        purchaseDate: {
          value: convertUIToISODate(eq.purchaseDate) || "",
          error: "",
        },
      });
      setModal({ isOpen: true, mode, data });
    } catch (err) {
      console.error("Failed to fetch equipment details:", err);
    }
  };

  const handleDeleteEquipment = (item) => {
    showAlert({
      title: "Are you sure you want to delete this equipment?",
      message: "This action cannot be undone.",
      type: "warning",
      confirmText: "Yes, Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        const res = await deleteCustomerEquipment(item.ID);

        if (res.code === 200) {
          notify("success", res?.message || "Equipment deleted successfully.");

          getCustomerEquipmentList(
            dispatch,
            decodedCustomerId,
            decodedLocationId,
            equipmentPage,
            equipmentFilters.filterParams.filters,
            equipmentFilters.filterParams.searchString,
          );
        } else {
          notify("danger", res?.message || "Failed to delete equipment!");
        }
      },
    });
  };

  const handleCloseModal = () => {
    setModal({
      isOpen: false,
      mode: "",
      data: null,
    });
    // setFormData(initialEquipmentFields);
  };

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prev) => {
      let newState = {
        ...prev,
        [name]: {
          ...prev[name],
          value: type === "checkbox" ? checked : value,
          error: "",
        },
      };

      if (name === "warranty" && !checked) {
        newState = {
          ...newState,
          standardWarranty: { value: false, error: "" },
          warrantyStartDate: { value: "", error: "" },
          warrantyEndDate: { value: "", error: "" },
        };
      }

      if (name === "standardWarranty" && checked) {
        const start = prev.warrantyStartDate.value;

        let endDate = "";
        if (start) {
          endDate = addYearsToISODate(start, 2);
        }

        newState = {
          ...newState,
          warrantyEndDate: { value: endDate, error: "" },
        };
      }

      if (name === "warrantyStartDate" && prev.standardWarranty.value) {
        let endDate = "";

        if (value) {
          const d = new Date(value);
          d.setFullYear(d.getFullYear() + 2);

          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");

          endDate = `${yyyy}-${mm}-${dd}`;
        }

        newState = {
          ...newState,
          warrantyEndDate: { value: endDate, error: "" },
        };
      }
      return newState;
    });
  };

  const handleSelectChange = (name, option) => {
    setFormData((prev) => {
      let updated = {
        ...prev,
        [name]: { value: option, error: "" },
      };

      switch (name) {
        case "manufacturer":
          updated.category = { value: null, error: "" };
          updated.product = { value: null, error: "" };
          updated.model = { value: null, error: "" };
          break;

        case "category":
          updated.product = { value: null, error: "" };
          updated.model = { value: null, error: "" };
          break;

        case "product":
          updated.model = option?.modelNo
            ? { value: option.modelNo, label: option.modelNo }
            : null;
          break;
      }
      return updated;
    });
  };

  const formattedEquipmentList =
    equipmentListData?.data?.CustomerEquipment?.map((item, index) => ({
      ID: item.ID,
      srNo:
        (equipmentPage - 1) *
        (equipmentListData?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      manufacturer: item.manufacturer?.name || "-",
      productCategory: item.productCategory,
      model: item.product?.modelNo || "-",
      product: item.product?.name || "-",
      serialNo: item.serialNo || "-",
      assetTag: item.assetTag || "-",
      seller: item.seller || "-",
      warranty: item.underWarranty,
      standardWarranty: item.standardWarranty,
      warrantyStartDate: item.warrantyStartDate || "",
      warrantyEndDate: item.warrantyEndDate || "",
    })) || [];

  const formattedServiceList =
    serviveHistoryData?.data?.job?.map((item, index) => ({
      ID: item.ID,
      srNo:
        (historyPageNum - 1) *
        (serviveHistoryData?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      jobNo: item.ID || "-",
      jobType: item.jobTypeID?.name || "-",
      description: item?.clientNote || "-",
      technicianAssigned: item?.technician?.name || "-",
      startDate: item.scheduledFrom || "-",
      endDate: item.scheduledTo || "-",
      status: item.jobStatus,
    })) || [];

  const historyColumns = [
    // { Header: "Sr.no.", accessor: "srNo", className: "col-srNo", },
    {
      Header: `${WORK_ORDER} No.`,
      accessor: "jobNo",
      className: "col-sjob-no",
      Cell: ({ row, state }) => (
        <span
          className="cell-link"
          onClick={() => jobDetailsNavigate(row.original)}
        >
          {row.original.ID}
        </span>
      ),
    },
    {
      Header: "Type",
      accessor: "jobType",
    },
    {
      Header: "Description",
      accessor: "description",
      Cell: ({ value }) => <div className="descriptions">{value}</div>,
    },

    {
      Header: "Technician Assigned",
      accessor: "technicianAssigned",
    },
    {
      Header: "Start Date",
      accessor: "startDate",
    },
    {
      Header: "End Date",
      accessor: "endDate",
    },
    // {
    //   Header: "Status",
    //   accessor: "status",
    //   Cell: ({ value }) => (
    //     <Badge color={value !== "CANCELLED" ? "success" : "warning"} pill>
    //       {value}
    //     </Badge>
    //   ),
    // },
  ];

  const jobDetailsNavigate = (rowData) => {
    const encodedId = urlEncodedId(rowData.ID);
    navigate(`/admin/jobs/${encodedId}/details`);
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container fluid className="mt-4">
        <Breadcrumbs
          items={[
            { name: "Customers", link: "/admin/customers" },
            {
              name:
                locationDetails.customerName === null ? (
                  <i className="fa fa-spinner fa-spin" />
                ) : (
                  locationDetails.customerName
                ),
              link: `/admin/customers/${encCustomerId}/details?tab=serviceLocation`,
            },
            { name: `${locationDetails.outlet}` },
          ]}
        />

        <Row>
          <Card className="m-3 shadow-sm border-0">
            <Nav className="custom-tabs">
              <NavItem>
                <NavLink
                  className={`text-center ${activeTab === "details" ? "active" : ""
                    }`}
                  href="#"
                  onClick={() => toggleTab("details")}
                >
                  Location Details
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`text-center ${activeTab === "equipments" ? "active" : ""
                    }`}
                  href="#"
                  onClick={() => toggleTab("equipments")}
                >
                  Customer Equipments
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`text-center ${activeTab === "history" ? "active" : ""
                    }`}
                  href="#"
                  onClick={() => toggleTab("history")}
                >
                  Service History
                </NavLink>
              </NavItem>
            </Nav>

            <CardBody>
              <TabContent activeTab={activeTab}>
                <TabPane tabId="details">
                  <Row className="mt-3">
                    <Col md={12} lg={6}>
                      <Row className="px-3">
                        <Col md={6} className="mb-2">
                          <FormGroup className="d-flex flex-column align-items-start">
                            <Label>Outlet</Label>
                            <span className="small-text">
                              {locationDetails.outlet}
                            </span>
                          </FormGroup>
                        </Col>

                        <Col md={6} className="mb-2">
                          <FormGroup className="d-flex flex-column align-items-start">
                            <Label>City</Label>
                            <span className="small-text">
                              {locationDetails.city}
                            </span>
                          </FormGroup>
                        </Col>

                        <Col md={6} className="mb-2">
                          <FormGroup className="d-flex flex-column align-items-start">
                            <Label>Region</Label>
                            <span className="small-text">
                              {locationDetails.region}
                            </span>
                          </FormGroup>
                        </Col>

                        <Col md={6} className="mb-2">
                          <FormGroup className="d-flex flex-column align-items-start">
                            <Label>Address</Label>
                            <span className="small-text">
                              {locationDetails.address}
                            </span>
                          </FormGroup>
                        </Col>

                        <Col md={6} className="mb-2">
                          <FormGroup className="d-flex flex-column align-items-start">
                            <Label>Contact Person</Label>
                            <span className="small-text">
                              {locationDetails.name}
                            </span>
                          </FormGroup>
                        </Col>

                        <Col md={6} className="mb-2">
                          <FormGroup className="d-flex flex-column align-items-start">
                            <Label>Email Address</Label>
                            <span className="small-text">
                              {locationDetails.email}
                            </span>
                          </FormGroup>
                        </Col>

                        <Col md={6} className="mb-2">
                          <FormGroup className="d-flex flex-column align-items-start">
                            <Label>Contact Number</Label>
                            <span className="small-text">
                              {locationDetails.mobile}
                            </span>
                          </FormGroup>
                        </Col>

                        <Col md={6} className="mb-2">
                          <FormGroup className="d-flex flex-column align-items-start">
                            <Label>Post Code</Label>
                            <span className="small-text">
                              {locationDetails.postCode || "-"}
                            </span>
                          </FormGroup>
                        </Col>

                        <Col md={6} className="mb-2">
                          <FormGroup className="d-flex flex-column align-items-start">
                            <Label>Alternate Contact Person</Label>
                            <span className="small-text">
                              {locationDetails.alt_name || "-"}
                            </span>
                          </FormGroup>
                        </Col>

                        <Col md={6} className="mb-2">
                          <FormGroup className="d-flex flex-column align-items-start">
                            <Label>Alternate Email Address</Label>
                            <span className="small-text">
                              {locationDetails.alt_email || "-"}
                            </span>
                          </FormGroup>
                        </Col>

                        <Col md={6} className="mb-2">
                          <FormGroup className="d-flex flex-column align-items-start">
                            <Label>Alternate Contact Number</Label>
                            <span className="small-text">
                              {locationDetails.alt_mobile || "-"}
                            </span>
                          </FormGroup>
                        </Col>
                      </Row>
                    </Col>
                    <Col
                      md={12}
                      lg={6}
                      className="d-flex justify-content-center align-items-center"
                    >
                      {isLoaded ? (
                        <GoogleMap
                          mapContainerClassName="map-container"
                          zoom={markerPosition ? 15 : 4}
                          center={markerPosition || defaultCenter}
                          onLoad={(mapInstance) => setMap(mapInstance)}
                          options={{
                            streetViewControl: false,
                            fullscreenControl: false,
                          }}
                        >
                          {markerPosition && (
                            <Marker position={markerPosition} />
                          )}
                        </GoogleMap>
                      ) : (
                        <div className="text-center">
                          <div
                            className="spinner-border text-default"
                            role="status"
                          ></div>
                          <div className="mt-2">Loading Map...</div>
                        </div>
                      )}
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="equipments">
                  {/* <div className="d-flex justify-content-start">
                    <Label>
                      Filters yet to be integrated{" "}
                      <span className="text-danger">*</span>
                    </Label>
                  </div> */}
                  <div className="d-flex justify-content-end mb-1">
                    <Button
                      color="default"
                      size="sm"
                      onClick={() => handleEquipments("Add", null)}
                    >
                      <i className="fa fa-plus mr-2" />
                      Add Equipment
                    </Button>
                  </div>

                  <div className="d-flex flex-wrap align-items-center w-100 mb-3 mt-3">
                    {/* Search */}
                    <Input
                      type="text"
                      placeholder="Search by serial no. or asset tag"
                      className="mr-2 mb-2 filter-input-large"
                      value={equipmentFilters.searchText}
                      onChange={(e) =>
                        setEquipmentFilters((prev) => ({
                          ...prev,
                          searchText: e.target.value,
                        }))
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleEquipmentSearch()
                      }
                      maxLength={150}
                    />

                    {/* Manufacturer */}
                    <Select
                      className="mr-2 mb-2 filter-select"
                      placeholder="Manufacturer"
                      options={manufacturersList}
                      getOptionLabel={(o) => o.name}
                      getOptionValue={(o) => o.ID}
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={equipmentFilters.manufacturer}
                      onChange={(selected) =>
                        setEquipmentFilters((prev) => ({
                          ...prev,
                          manufacturer: selected,
                          category: null,
                          model: null,
                        }))
                      }
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      placeholder="Category"
                      options={filterCategoryOptions}
                      getOptionLabel={(o) => o.category}
                      getOptionValue={(o) => o.ID}
                      value={equipmentFilters.category}
                      onChange={(selected) =>
                        setEquipmentFilters((prev) => ({
                          ...prev,
                          category: selected,
                          product: null,
                          model: null,
                        }))
                      }
                      isDisabled={!equipmentFilters.manufacturer}
                      styles={fixedWidthCustomStyle()}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      placeholder="Product"
                      options={filterProductOptions}
                      getOptionLabel={(o) => o.name}
                      getOptionValue={(o) => o.ID}
                      value={equipmentFilters.product}
                      onChange={(selected) =>
                        setEquipmentFilters((prev) => ({
                          ...prev,
                          product: selected,
                          model: null,
                        }))
                      }
                      isDisabled={!equipmentFilters.category}
                      styles={fixedWidthCustomStyle()}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      placeholder="Model"
                      options={filterModelOptions}
                      getOptionLabel={(o) => o.modelNo}
                      getOptionValue={(o) => o.modelNo}
                      value={equipmentFilters.model}
                      onChange={(selected) =>
                        setEquipmentFilters((prev) => ({
                          ...prev,
                          model: selected,
                        }))
                      }
                      isDisabled={!equipmentFilters.product}
                      styles={fixedWidthCustomStyle()}
                    />

                    {/* Seller */}
                    <Select
                      className="mr-2 mb-2 filter-select"
                      placeholder="Seller"
                      options={sellerOptions}
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={equipmentFilters.seller}
                      onChange={(selected) =>
                        setEquipmentFilters((prev) => ({
                          ...prev,
                          seller: selected,
                        }))
                      }
                    />

                    {/* <div className="w-100"></div> */}

                    {/* Warranty */}
                    <Select
                      className="mr-2 mb-2 filter-select"
                      placeholder="Warranty"
                      options={[
                        { value: true, label: "Yes" },
                        { value: false, label: "No" },
                      ]}
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={equipmentFilters.warranty}
                      onChange={(selected) =>
                        setEquipmentFilters((prev) => ({
                          ...prev,
                          warranty: selected,
                        }))
                      }
                    />

                    {/* <Select
                      className="mr-2 mb-2 filter-select"
                      placeholder="Standard warranty"
                      options={[
                        { value: true, label: "Yes" },
                        { value: false, label: "No" },
                      ]}
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle(true)}
                      value={equipmentFilters.standardWarranty}
                      onChange={(selected) =>
                        setEquipmentFilters((prev) => ({
                          ...prev,
                          standardWarranty: selected,
                        }))
                      }
                    /> */}

                    {/* Search Button */}
                    <Button
                      color="default"
                      className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                      onClick={handleEquipmentSearch}
                    >
                      {equipmentListState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {/* Clear */}
                    {(equipmentFilters.searchText ||
                      equipmentFilters.manufacturer ||
                      equipmentFilters.category ||
                      equipmentFilters.model ||
                      equipmentFilters.seller ||
                      equipmentFilters.warranty) && (
                        <Button
                          color="default"
                          className="d-flex align-items-center justify-content-center mt-2 mb-3 filter-btn btn-simple"
                          onClick={handleEquipmentClear}
                        >
                          <i className="fa fa-times mr-1" /> Clear
                        </Button>
                      )}
                  </div>

                  <Col md={12}>
                    {equipmentListState.isLoading ? (
                      <div className="loaderContainer">
                        <i className="fa fa-spinner fa-spin fa-2xl loader" />
                      </div>
                    ) : equipmentListState.error ? (
                      <div className="loaderContainer text-danger">
                        {equipmentListState.error}
                      </div>
                    ) : equipmentListState.noRecords ? (
                      <div className="loaderContainer text-muted">
                        No customer equipments added yet.
                      </div>
                    ) : (
                      <CommonTable
                        data={formattedEquipmentList}
                        resizable={false}
                        columns={columns}
                        defaultPageSize={
                          equipmentListData?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE
                        }
                        showPaginationBottom={true}
                        className="-striped -highlight table-hover"
                        activePage={equipmentPage}
                        totalPages={Math.ceil(
                          (equipmentListData?.totalFilterRecords || 0) /
                          (equipmentListData?.noOfRecordsPerPage ||
                            DEFAULT_RECORDS_PER_PAGE),
                        )}
                        setPageNum={setEquipmentPage}
                      />
                    )}
                  </Col>
                </TabPane>
                <TabPane tabId="history">
                  <div className="mt-3">
                    {serviceListState?.isLoading ? (
                      <div className="loaderContainer">
                        <i className="fa fa-spinner fa-spin fa-2xl loader" />
                      </div>
                    ) : serviceListState?.error ? (
                      <div className="loaderContainer text-danger">
                        {serviceListState?.error}
                      </div>
                    ) : serviceListState?.noRecords ? (
                      <div className="loaderContainer text-muted">
                        No customer equipments added yet.
                      </div>
                    ) : (
                      <CommonTable
                        data={formattedServiceList}
                        resizable={false}
                        columns={historyColumns}
                        defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                        showPaginationBottom={true}
                        className="-striped -highlight table-hover "
                        activePage={historyPageNum}
                        totalPages={Math.ceil(
                          (serviveHistoryData?.data?.totalFilterRecords || 0) /
                          (serviveHistoryData?.data?.noOfRecordsPerPage ||
                            DEFAULT_RECORDS_PER_PAGE),
                        )}
                        setPageNum={sethistoryPageNum}
                      />
                    )}
                  </div>
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Row>
        <RightPanelCard isOpen={modal.isOpen} onClose={handleCloseModal}>
          <Card className="mb-0">
            <CardHeader
              className="d-flex align-items-center justify-content-between mb-2"
              tag="h4"
            >
              {modal.mode} Equipment
              <i
                className="fa fa-close text-muted pointer"
                onClick={handleCloseModal}
              />
            </CardHeader>

            <CommonEquipmentForm
              formData={formData}
              handleInputChange={handleInputChange}
              handleSelectChange={handleSelectChange}
              handleEquipmentBlur={handleEquipmentBlur}
            />

            <CardFooter className="d-flex justify-content-end mb-0">
              <Button
                color="secondary"
                onClick={handleCloseModal}
                className="mr-2"
              >
                Cancel
              </Button>
              <Button
                color="default"
                type="submit"
                onClick={handleSaveEquipment}
                disabled={Object.values(formData).some((field) => field.error)}
              >
                {showLoader ? (
                  <i className="fa fa-spinner fa-spin" />
                ) : (
                  "Save"
                )}
              </Button>
            </CardFooter>
          </Card>
        </RightPanelCard>
      </Container>
    </>
  );
}

export default LocationDetails;
